/** What the basket actually did, minute by minute.
 *
 * The payoff chart beside it is a hypothetical across PRICE. This is the
 * realised path through TIME, and it is the only one of the two that can
 * show a drawdown — a basket that ended flat after being 60 points up at
 * 09:17 looks identical to one that never moved, until you draw this.
 *
 * Inline SVG, no chart library: the house style here (the reports equity
 * curve does the same), and a line with a marker does not justify a
 * dependency.
 */

import type { ReactElement } from "react";
import { cn } from "@/lib/cn";
import type { SimMtm } from "@/lib/simulatorApi";

const W = 720;
const H = 180;
const PAD = 8;

interface Props {
  mtm: SimMtm;
  /** The minute the rest of the page is showing, tracked as a marker so
   * stepping the clock and reading the curve stay in step. */
  at: string;
}

interface Plot {
  path: string;
  zeroY: number;
  markerX: number | null;
  markerY: number | null;
}

function plot(mtm: SimMtm, at: string): Plot | null {
  const marked = mtm.points.filter((p) => p.mtm !== null);
  if (marked.length < 2) return null;
  const values = marked.map((p) => p.mtm ?? 0);
  // Zero is always in frame: a curve that never crosses it still has to
  // show WHICH side of it the basket lived on.
  const lo = Math.min(0, ...values);
  const hi = Math.max(0, ...values);
  const span = hi - lo || 1;
  const y = (v: number): number => PAD + (H - 2 * PAD) * (1 - (v - lo) / span);
  const x = (i: number): number => PAD + ((W - 2 * PAD) * i) / (mtm.points.length - 1);

  let path = "";
  let markerX: number | null = null;
  let markerY: number | null = null;
  mtm.points.forEach((point, i) => {
    if (point.mtm === null) return;
    path += `${path === "" ? "M" : "L"}${x(i).toFixed(1)},${y(point.mtm).toFixed(1)} `;
    if (point.at === at) {
      markerX = x(i);
      markerY = y(point.mtm);
    }
  });
  return { path: path.trim(), zeroY: y(0), markerX, markerY };
}

function money(value: number | null, basis: SimMtm["basis"]): string {
  if (value === null) return "—";
  return basis === "rupees" ? `₹${value.toFixed(0)}` : `${value.toFixed(1)} pts`;
}

function Extremes({ mtm }: Readonly<{ mtm: SimMtm }>): ReactElement {
  return (
    <div className="flex flex-wrap gap-x-6 gap-y-1 text-xs">
      <span className="text-ink-secondary">
        Peak{" "}
        <span className="font-mono tabular-nums text-success">
          {money(mtm.max_profit, mtm.basis)}
        </span>
        {mtm.max_profit_at !== null && (
          <span className="text-ink-muted"> at {mtm.max_profit_at}</span>
        )}
      </span>
      <span className="text-ink-secondary">
        Worst{" "}
        <span className="font-mono tabular-nums text-danger">{money(mtm.max_loss, mtm.basis)}</span>
        {mtm.max_loss_at !== null && <span className="text-ink-muted"> at {mtm.max_loss_at}</span>}
      </span>
      <span className="text-ink-secondary">
        Close{" "}
        <span
          className={cn(
            "font-mono tabular-nums",
            (mtm.final ?? 0) >= 0 ? "text-success" : "text-danger",
          )}
        >
          {money(mtm.final, mtm.basis)}
        </span>
      </span>
    </div>
  );
}

export function MtmChart({ mtm, at }: Readonly<Props>): ReactElement {
  const shape = plot(mtm, at);
  if (shape === null) {
    return (
      <p className="text-sm text-ink-secondary">
        Nothing to plot yet — the basket has no marked minutes in this session.
      </p>
    );
  }
  return (
    <div className="flex flex-col gap-2">
      <div className="flex items-baseline justify-between">
        <h3 className="text-sm font-medium text-ink">P&amp;L through the session</h3>
        <Extremes mtm={mtm} />
      </div>
      <svg
        viewBox={`0 0 ${String(W)} ${String(H)}`}
        className="w-full"
        role="img"
        aria-label="Basket profit and loss across the session"
      >
        <line
          x1={PAD}
          x2={W - PAD}
          y1={shape.zeroY}
          y2={shape.zeroY}
          className="stroke-line-strong"
          strokeDasharray="3 3"
          strokeWidth={1}
        />
        <path d={shape.path} fill="none" className="stroke-accent" strokeWidth={1.75} />
        {shape.markerX !== null && shape.markerY !== null && (
          <circle cx={shape.markerX} cy={shape.markerY} r={4} className="fill-accent" />
        )}
      </svg>
      <div className="flex justify-between font-mono text-[11px] text-ink-muted">
        <span>{mtm.points[0]?.at}</span>
        <span>{mtm.points[mtm.points.length - 1]?.at}</span>
      </div>
    </div>
  );
}
